/**
 * Knuska mega navigation.
 *
 * Deliberately independent of the theme's menu component: it shares no
 * classes, no state and no timers with it, so the two cannot interfere.
 *
 * Behaviour:
 * - Hover opens after a short delay, so crossing the bar does not flash
 *   every panel open in turn.
 * - Leaving has a grace period, so the diagonal move from the label down
 *   into the panel does not close it.
 * - Keyboard: Enter/Space toggles, Escape closes and returns focus, and
 *   tabbing out of a panel closes it.
 * - Touch: the first tap opens rather than following the link.
 */
if (!customElements.get('knuska-meganav')) {
  class KnuskaMegaNav extends HTMLElement {
    connectedCallback() {
      this.openDelay = Number.parseInt(this.dataset.openDelay, 10) || 0;
      this.closeDelay = Number.parseInt(this.dataset.closeDelay, 10) || 0;
      this.items = Array.from(this.querySelectorAll('.knuska-mn__item--has-panel'));
      this.scrim = this.querySelector('.knuska-mn__scrim');
      this.current = null;
      this.openTimer = null;
      this.closeTimer = null;

      this.items.forEach((item) => {
        const trigger = item.querySelector('.knuska-mn__top');
        if (!trigger) return;

        item.addEventListener('mouseenter', () => this.scheduleOpen(item));
        item.addEventListener('mouseleave', () => this.scheduleClose());
        trigger.addEventListener('click', (evt) => {
          evt.preventDefault();
          if (item === this.current) {
            this.close();
          } else {
            this.open(item);
          }
        });
      });

      this.addEventListener('keydown', (evt) => {
        if (evt.key !== 'Escape' || !this.current) return;
        const trigger = this.current.querySelector('.knuska-mn__top');
        this.close();
        if (trigger) trigger.focus();
      });

      this.addEventListener('focusin', (evt) => {
        const item = evt.target.closest('.knuska-mn__item--has-panel');
        if (item) {
          this.open(item);
        } else {
          this.close();
        }
      });

      // A click anywhere else closes; the panel itself is inside `this`.
      this.documentClickHandler = (evt) => {
        if (this.current && !this.contains(evt.target)) this.close();
      };
      document.addEventListener('click', this.documentClickHandler);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.documentClickHandler);
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
    }

    scheduleOpen(item) {
      clearTimeout(this.closeTimer);
      clearTimeout(this.openTimer);
      // A panel is already open, so the visitor is scanning the bar: swap
      // immediately rather than making them wait out the delay again.
      const delay = this.current ? 0 : this.openDelay;
      this.openTimer = setTimeout(() => this.open(item), delay);
    }

    scheduleClose() {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => this.close(), this.closeDelay);
    }

    open(item) {
      if (item === this.current) return;
      if (this.current) this.hide(this.current);

      const panel = item.querySelector('.knuska-mn__panel');
      const trigger = item.querySelector('.knuska-mn__top');
      if (!panel) return;

      panel.hidden = false;
      // Let the browser register the pre-transition state before animating.
      requestAnimationFrame(() => item.classList.add('is-open'));
      if (trigger) trigger.setAttribute('aria-expanded', 'true');

      this.current = item;
      this.classList.add('is-active');
      if (this.scrim) this.scrim.hidden = false;
    }

    close() {
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      if (this.current) this.hide(this.current);
      this.current = null;
      this.classList.remove('is-active');
      if (this.scrim) this.scrim.hidden = true;
    }

    hide(item) {
      const panel = item.querySelector('.knuska-mn__panel');
      const trigger = item.querySelector('.knuska-mn__top');
      item.classList.remove('is-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (panel) panel.hidden = true;
    }
  }

  customElements.define('knuska-meganav', KnuskaMegaNav);
}
